import React, { useMemo, useState } from "react";

import area from "@turf/area";
import centerOfMass from "@turf/center-of-mass";

import Color from "@mapmycustomers/shared/enum/Color";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { Feature, useMap, useMapEventHandler } from "@app/component/map/FeaturedMap";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import defaultIdGetter from "@app/util/defaultIdGetter";
import { TERRITORY_LABEL_ZINDEX } from "@app/util/map/consts";
import getScaledSize from "@app/util/map/markerStyles/getScaledSize";
import svgToDataUrl from "@app/util/map/svgToDataUrl";
import { linearScale } from "@app/util/scale";

import getSvgForLabel from "../../util/getSvgForLabel";

interface TerritoryLabel extends Pick<Territory, "color" | "id" | "name"> {
  position: LongLat;
}

const territoryLabelGeometryGetter = (
  label: TerritoryLabel
): Exclude<google.maps.Data.FeatureOptions["geometry"], undefined> =>
  new google.maps.Data.Point({ lat: label.position[1], lng: label.position[0] });

const shouldRedrawLabel = (a: TerritoryLabel, b: TerritoryLabel) =>
  a.id !== b.id || a.color !== b.color || a.name !== b.name;

const getZoomThresholdByArea = (area: number) => Math.floor(linearScale(1e4, 1e9, 13, 3, area));

interface Props {
  selectedTerritoryId?: Territory["id"];
  territories: Territory[];
  type: string;
  zIndex?: number;
}

const TerritoryLabelFeature: React.FC<Props> = ({
  selectedTerritoryId,
  territories,
  type,
  zIndex = TERRITORY_LABEL_ZINDEX,
}) => {
  const { map } = useMap();

  const territoryLabelStyleGetter = useDynamicCallback(
    (label: TerritoryLabel): google.maps.Data.StyleOptions => {
      const color = anyColorToHex(label.color ?? Color.BLACK);
      const normalView = !isDefined(selectedTerritoryId) || selectedTerritoryId === label.id;
      const { anchor, size, svg } = getSvgForLabel(label.name, color, normalView ? 1 : 0.75);
      const scale = 1.25;

      return {
        clickable: false,
        icon: {
          anchor: new google.maps.Point(...getScaledSize(anchor, scale)),
          scaledSize: new google.maps.Size(...getScaledSize(size, scale)),
          url: svgToDataUrl(svg),
        },
        zIndex,
      };
    }
  );

  const [zoom, setZoom] = useState(map.getZoom()!);
  useMapEventHandler("zoom_changed", () => {
    setZoom(map.getZoom()!);
  });

  const territoryLabels = useMemo(() => {
    return territories
      .map<TerritoryLabel | undefined>(({ id, color, name, territoryDetail }) => {
        const geometry =
          territoryDetail.type === "points" ? territoryDetail.points : territoryDetail.shape;

        if (!geometry) {
          return undefined;
        }

        // TODO: think about caching area and centerOfMass results
        const territoryArea = area({ geometry, properties: null, type: "Feature" });
        const zoomThreshold = getZoomThresholdByArea(territoryArea);
        // do not show labels for small territories on small zoom levels
        if (zoom < zoomThreshold) {
          return undefined;
        }

        return {
          id,
          color,
          name,
          position: centerOfMass(geometry).geometry.coordinates as LongLat,
        };
      })
      .filter(isDefined);
  }, [territories, zoom]);

  return (
    <Feature<TerritoryLabel>
      geometryGetter={territoryLabelGeometryGetter}
      idGetter={defaultIdGetter}
      isDifferent={shouldRedrawLabel}
      items={territoryLabels}
      styleGetter={territoryLabelStyleGetter}
      type={type}
    />
  );
};

export default TerritoryLabelFeature;
